import React from "react";
import CallIcon from "@mui/icons-material/Call";
import { Button, Grid } from "@mui/material";
import {
  ControlledCustomSelect,
  CustomFormLabel,
  CustomTextField,
} from "../../formsComponents";
import { useFormik } from "formik";
import { RightPanel } from "../../RightPanel";
import { useDispatchWrapper } from "../../../hooks";
import {
  sendVoiceCalling,
} from "../../../redux/common/commonActions";
import { ICallDialogProps } from "./CallDialog.types";
import { useSelector } from "react-redux";
import { IStoreState } from "../../../redux/initialStoreState";
import { fetchTemplateListAllTempsAsync } from "../../../redux/templates/templateActions";
import { ICreateTemplate } from "../../../redux/templates/templates.types";
import { api } from "../../../api/api";
import { QuillEditor } from "../../QuillEditor/QuillEditor";
import { openDialerWithNumber } from "../../../redux/webrtc/webrtcSlice";
import { WEBRTC_ENABLED } from "../../../contexts/WebrtcProvider/WebrtcProvider";
import { setPendingCallScope } from "../../../redux/mirtapbxCallContext";

export const CallDialog: React.FC<ICallDialogProps> = (props) => {
  const { mobileNumber, customer_id, customer_policy_id, lead_id } = props;
  const [open, setOpen] = React.useState(false);
  const dispatch = useDispatchWrapper();

  const handleToggleOpen = () => {
    if (WEBRTC_ENABLED) {
      // Stamp the CRM scope BEFORE the softphone opens. When the user presses
      // Call in the widget, SoftphoneWidget's placeCall helper reads this scope
      // from Redux, POSTs /mirtapbx/calls/context, then fires sipClient.call().
      // Only dispatched when at least one CRM id is present — otherwise the
      // widget keeps its default empty-scope behaviour.
      const toInt = (v: number | string | null | undefined): number | null => {
        if (v === null || v === undefined || v === "") return null;
        const n = typeof v === "number" ? v : Number(v);
        return Number.isFinite(n) ? n : null;
      };
      const cid = toInt(customer_id);
      const cpid = toInt(customer_policy_id);
      if (cid || cpid || lead_id) {
        dispatch(
          setPendingCallScope({
            customer_id: cid,
            customer_policy_id: cpid,
            lead_id: lead_id ?? null,
          }),
        );
      }
      // Open the softphone dialer with the number pre-filled
      dispatch(openDialerWithNumber(mobileNumber));
    } else {
      setOpen(!open);
    }
  };

  return (
    <>
      <CallIcon color="primary" fontSize="small" onClick={handleToggleOpen} sx={{ cursor: "pointer" }} />
      {open && !WEBRTC_ENABLED && (
        <CallRightPanel open={open} mobileNumber={mobileNumber} onClose={() => setOpen(false)}/>
      )}
    </>
  );
};

export const CallRightPanel: React.FC<{
  open: boolean;
  mobileNumber: string;
  onClose: () => void;
}> = ({ open, mobileNumber, onClose }) => {
  const dispatch = useDispatchWrapper();
  const [loading, setLoading] = React.useState(false);
  const [selectedTemplate, setSelectedTemplate] = React.useState("");
  const templates = useSelector(
    (storeState: IStoreState) => storeState.templates.list
  );
  const { values,setFieldValue, handleChange, handleSubmit } = useFormik({
    initialValues: {
      mobileNumber: mobileNumber,
      template: "",
      message: "",
    },
    onSubmit: async (values) => {
      setLoading(true);
      dispatch(
        sendVoiceCalling(
          {
            mobileNumber: values.mobileNumber,
          },
          (isSucess) => {
            if (isSucess) {
              onClose();
            }
            setLoading(false);
          }
        )
      );
    },
  });

  React.useEffect(() => {
    dispatch(fetchTemplateListAllTempsAsync());
  }, []);

  React.useEffect(() => {
    const fetchTemplate = async (temp: ICreateTemplate) => {
      try {
        const res = await api.get(
          `/templates/get-templates?templates_id=${temp.templates_id}`
        );
        const list: ICreateTemplate[] = res.data.data;
        if (list.length > 0) {
          setFieldValue("message", list[0].body);
        }
      } catch (err) {
        console.log(err);
      }
    };
    if (selectedTemplate) {
      const template = templates.find(
        (x) => x.template_name === selectedTemplate
      );
      if (template) {
        fetchTemplate(template);
      }
    }
  }, [selectedTemplate]);


  return (
    <RightPanel
      open={open}
      heading="Send Message as Voice Calling"
      subHeading="Connecting Through Voice Calling: Delivering Messages to Specified Mobile Numbers"
      onClose={onClose}
      isWrappedWithForm
      onFormSubmit={handleSubmit}
      actionButtons={() => {
        return (
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Button
                variant="contained"
                type="submit"
                disabled={loading}
                fullWidth
              >
                Send
              </Button>
            </Grid>
            <Grid item xs={12} md={4}>
              <Button variant="contained" fullWidth onClick={onClose}>
                Cancel
              </Button>
            </Grid>
          </Grid>
        );
      }}
    >
      <Grid container>
        <Grid item xs={12} md={12}>
          <CustomFormLabel>Mobile Number(+1)</CustomFormLabel>

          <CustomTextField disabled fullWidth value={values.mobileNumber} />
        </Grid>
        <Grid item xs={12} md={12}>
      <CustomFormLabel>Template</CustomFormLabel>
          <ControlledCustomSelect
            fullWidth
            value={selectedTemplate}
            name="sub_mobules"
            onChange={(e) => setSelectedTemplate(e.target.value as string)}
            placeholder="Choose Template"
            displayEmpty
            options={templates.map((item) => {
              return { label: item.template_name, value: item.template_name };
            })}
          ></ControlledCustomSelect>
      </Grid>
      <Grid item xs={12} md={12}>
        <CustomFormLabel>Message</CustomFormLabel>

        <QuillEditor
            value={values.message}
            onChange={(html) => setFieldValue("message", html)}
          />
      </Grid>
      </Grid>
    </RightPanel>
  );
};
